<template>
  <div
    class="flex relative bg-neutral-background-secondary px-7.5 py-5 space-x-6"
  >
    <div class="flex-1">
      <template v-if="review.includedReviewItems.value.length">
        <div
          class="inline-grid [grid-template-columns:auto_1fr] border border-neutral-border-default rounded-lg"
        >
          <div
            class="bg-neutral-background-default h-12.5 rounded-tl-lg sticky top-13 left-0 z-51"
          >
            <div
              class="h-full text-sm px-5 py-3.5 justify-between border-neutral-border-default border-b flex items-center font-medium"
            >
              <span class="text-neutral-text-title">Records</span>
              <div class="flex items-center gap-2">
                <Sort />
                <ReviewItemFilters />
              </div>
            </div>
          </div>

          <div
            class="bg-neutral-background-default rounded-tr-lg h-12.5 sticky top-13 left-0 z-40"
          >
            <div
              class="h-full text-sm border-l border-b border-neutral-border-default text-neutral-text-title flex p-3.5 items-center font-medium justify-between"
            >
              <div class="truncate">
                {{ appraisalTabs[selectedAppraisalTabIndex].label }}
              </div>
            </div>
          </div>

          <template v-if="paginatedIncludedReviewItems.length">
            <ReviewItemAppraisal
              v-for="reviewItem in paginatedIncludedReviewItems"
              :key="reviewItem.id"
              :review-item="reviewItem"
              :is-metadata-expanded="isMetadataExpanded"
              :active-appraisal-key="
                appraisalTabs[selectedAppraisalTabIndex].key
              "
            />
          </template>
          <div v-else class="col-span-2 p-4 text-center text-gray-500">
            No articles match your filters
          </div>
        </div>
        <div
          v-if="review.filteredIncludedReviewItems.value.length > 0"
          class="w-full flex items-center justify-between px-5 py-4 sticky bottom-0 bg-neutral-background-default border-r border-l border-b border-neutral-border-default rounded-b-lg z-50"
        >
          <p class="text-base font-medium text-neutral-text-title">
            Total Records: {{ review.filteredIncludedReviewItems.value.length }}
          </p>
          <Pagination
            v-model:items-per-page="itemsPerPage"
            v-model:page="currentPage"
            :total-items="review.filteredIncludedReviewItems.value.length"
          />
        </div>
      </template>

      <div v-else class="p-6">This review is empty</div>
    </div>

    <div
      v-if="paginatedIncludedReviewItems.length"
      class="w-[260px] sticky top-13 right-0 h-[calc(100vh-80px)] overflow-y-auto flex flex-col items-start gap-3 z-1"
    >
      <button
        v-for="(tab, index) in appraisalTabs"
        :key="tab.key"
        class="px-3.5 py-2 w-full flex justify-between rounded-lg text-left transition-colors hover:bg-white hover:shadow hover:shadow-black/10"
        :class="{
          'bg-white shadow shadow-black/10 text-black':
            index === selectedAppraisalTabIndex,
          'text-gray-400': index !== selectedAppraisalTabIndex,
        }"
        @click="selectedAppraisalTabIndex = index"
      >
        <span class="text-base font-medium truncate">{{ tab.label }}</span>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch, onActivated, onDeactivated } from 'vue'
import { injectStrict } from '@app/utils/injectStrict'
import Sort from '@app/views/Review/Execute/Sort/Sort.vue'
import ReviewItemFilters from '../SearchAndFilters/ReviewItemFilters.vue'
import ReviewItemAppraisal from './ReviewItemAppraisal/ReviewItemAppraisal.vue'
import { useStorage } from '@vueuse/core'
import { useRoute, useRouter } from 'vue-router'
import { ReviewKey } from '../../use-review'
import Pagination from '@app/components/Global/Pagination/Pagination.vue'

const review = injectStrict(ReviewKey)
const route = useRoute()
const router = useRouter()

const currentPage = computed({
  get: () => Number(route.query.page) || 1,
  set: (value) =>
    router.replace({ query: { ...route.query, page: value.toString() } }),
})

watch(currentPage, (newPage) => {
  router.replace({
    query: { ...route.query, page: newPage.toString() },
  })
})

const itemsPerPage = computed({
  get: () => Number(route.query.itemsPerPage) || 5,
  set: (value) =>
    router.replace({
      query: { ...route.query, itemsPerPage: value.toString() },
    }),
})

const paginatedIncludedReviewItems = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage.value
  return review.filteredIncludedReviewItems.value.slice(
    start,
    start + itemsPerPage.value,
  )
})

type AppraisalKey = 'imdrf' | 'oxford'

interface AppraisalTab {
  key: AppraisalKey
  label: string
}

const appraisalTabs = computed<AppraisalTab[]>(() => {
  const tabs: AppraisalTab[] = []

  if (review.entity.value.plan?.appraisalPlan.isImdrfMdce2019Applicable) {
    tabs.push({ key: 'imdrf', label: 'IMDRF MDCE 2019' })
  }
  if (
    review.entity.value.plan?.appraisalPlan.isOxfordLevelOfEvidenceApplicable
  ) {
    tabs.push({ key: 'oxford', label: 'Oxford LOE' })
  }

  return tabs
})

const selectedAppraisalTabIndex = ref(0)

const isMetadataExpanded = useStorage(
  'isMetadataExpanded-' + review.entity.value.id,
  true,
)

const isInExecuteStep = ref(true)
onDeactivated(() => (isInExecuteStep.value = false))
onActivated(() => (isInExecuteStep.value = true))

watch(
  () => review.filtering.terms.value,
  () => {
    currentPage.value = 1
  },
)
watch(
  () => review.filtering.filters.value,
  () => {
    currentPage.value = 1
  },
  { deep: true },
)
watch(itemsPerPage, () => {
  currentPage.value = 1
})
</script>
